import { ProductAttributeMap, TagOnTopRightValues } from "~/types/ecom/product/attribute-map";
import type { Product, ProductAttribute } from "~/types/ecom/product/product.type";

// --------------------- Get product ratings from a scale of 1 to 5 ------------------------ //
export function getProductRating(attributes: ProductAttribute[]): string | null {
  if(!attributes) return null;

  const attribute = attributes.find(
    (attr) => attr.id === ProductAttributeMap.ReviewRating
  );
  return attribute ? attribute.value : null;
}

// --------------------- Get Product Additional Images ------------------------------------//
export function getProductImages(attributes: ProductAttribute[]): string[] {
  if (!Array.isArray(attributes)) return [];
  return attributes
    .filter((attr) => attr.id === ProductAttributeMap.AdditionalPhotos)
    .map((attr) => attr.value)
}

// --------------------- Get customer review numbers for the product ------------------------ //
export function getNumberOfReviews(attributes: ProductAttribute[]): string | null {
  if(!attributes) return null;

  const attribute = attributes.find(
    (attr) => attr.id === ProductAttributeMap.NumberOfReviews
  );
  return attribute ? attribute.value : null;
}

// --------------------- Get minimum allowable quantity information ------------------------ //
export function getQuantityMinimum(attributes: ProductAttribute[]): string | null {
  const attribute = attributes.find(
    (attr) => attr.id === ProductAttributeMap.QuantityMinimum
  );
  return attribute ? attribute.value : null;
}

// --------------------- Get delivery timeline for dropship delivery ------------------------ //
export function getDropshipDeliveryTime(attributes: ProductAttribute[]): string | null {
  const attribute = attributes.find(
    (attr) => attr.id === ProductAttributeMap.DropshipDeliveryTime
  );
  return attribute ? attribute.value : null;
}

// --------------------- Get offer value to be displayed on top right of the card ------------------------ //
export function getTopRightTagValues(attributes: ProductAttribute[]): { value: string, color: string }[] | null {
  // @multiple web image top right overlay for same product are found
  if(!attributes) return null;

  const colorMap: Record<number, string> = {
    [ProductAttributeMap.RedTag]: "danger", 
    [ProductAttributeMap.BlueTag]: "info",  
    [ProductAttributeMap.GreenTag]: "success",
    [ProductAttributeMap.BlackTag]: "dark",
    [ProductAttributeMap.YellowTag]: "warning" 
  };

  const topRightTagValues = attributes
    .filter((attr) => TagOnTopRightValues.includes(attr.id)) 
    .map((attr) => ({
      value: attr.value,
      color: colorMap[attr.id] || "info" 
    }));

  return topRightTagValues.length ? topRightTagValues : null;
}

// --------------------- Get detailed description HTML ------------------------ //
export function getDetailedDescriptionHTML(attributes: ProductAttribute[]): string {
  // @multiple detailed descriptions for same product are found
  const attributeValues = attributes
    .filter((attr) => attr.id === ProductAttributeMap.DetailedDescription)
    .map((attr) => attr.value);

  return attributeValues.length ? attributeValues.join("").replace(/\n/g, "<br>") : "";
}


export function getPromotionalMessages(attributes: ProductAttribute[]): string[] | null {
  // @multiple web image top right overlay for same product are found
  if(!attributes){
    return null;
  }
  const promoMessages: string[] = attributes
    .filter((attr) => attr.id === ProductAttributeMap.PromotionMessage)
    .map((attr) => attr.value);

  return promoMessages.length ? promoMessages : null;
}

/**
 * Checks if the "Notify Me" option is available for a product.
 * @param {Product} product - The ECOM product to check against.
 * @returns {boolean} - Returns value of "allow_stock_notifications" flag
 */
export function checkNotifyMeOption(product: Product): boolean {
  return Boolean(product.allow_stock_notifications); // true, false, undefined
}


export function getCompareOption(attributes: ProductAttribute[]): string | null {
  if(!attributes){
    return "";
  }
  const attribute = attributes
    .filter((attr) => attr.id === ProductAttributeMap.CompareOption)
    .map((attr) => attr.value);
  return attribute.length ? attribute.join("").replace(/\n/g, "<br>") : "";
}


export function getKeyFeatures(attributes: ProductAttribute[], featureKey: string): string[] | null {
  if (!attributes) {
    return null;
  }

  const attribute = attributes.find((attr) => {
    return attr.key.toLowerCase() === featureKey.toLowerCase(); // Compares the values
  });

  if (attribute && attribute.value) {
    return attribute.value
      .split(/(?:•\s?|_\s?|- \s?)+/) // Matches `•`, `• `, `_`, `_ `, and `- `
      .map(item => item.trim())
      .filter(item => item !== '');
  }

  return null;
}

export function getPDPDescription(attribute: string): string[] | null {
  if (!attribute) {
    return null;
  }

  return attribute
    .split(/(?:•\s?|\n|_\s?|- \s?)+/) // Split by `•`, new lines, `_`, or `- `
    .map(item => item.trim())         // Trim each item
    .filter(item => item !== '');     // Remove empty items
}

export function getProductLocaleName(product,locale,locales) {
  if(!product){
    return "";
  }
  const currentLocale = findInArrayByKey(locales,'code',locale);
  if(!product.locales || !product.locales[currentLocale.ecom_api_code] ||
      !product.locales[currentLocale.ecom_api_code].name){
    return product.full_name ??  product.name;
  }
  return product.locales[currentLocale.ecom_api_code].name +' '+ (product.locales[currentLocale.ecom_api_code].type ?? '')
}

/**
 * verify user has accpted `functional` cookies to load videoly script in PDP
 */

export enum AlgoliaProductAttributeMap {
  red_tag = 10098,
  blue_tag = 10096,
  green_tag = 10097,
  yellow_tag = 10062,
  black_tag = 10063,
}

export const algoliaTagsColorMap: Record<number, string> = {
  [AlgoliaProductAttributeMap.red_tag]: "danger",
  [AlgoliaProductAttributeMap.blue_tag]: "info",
  [AlgoliaProductAttributeMap.green_tag]: "success",
  [AlgoliaProductAttributeMap.black_tag]: "dark",
  [AlgoliaProductAttributeMap.yellow_tag]: "warning",
};

export const algoliaTagsWithVariants = (obj: any) => {
  return Object.entries({
    [AlgoliaProductAttributeMap.blue_tag]: obj.blue_tag,
    [AlgoliaProductAttributeMap.red_tag]: obj.red_tag,
    [AlgoliaProductAttributeMap.yellow_tag]: obj.yellow_tag,
    [AlgoliaProductAttributeMap.green_tag]: obj.green_tag,
    [AlgoliaProductAttributeMap.black_tag]: obj.black_tag,
  })
    .filter(([_, value]) => Boolean(value)) 
    .map(([key, value]) => ({
      name: value,
      variant: algoliaTagsColorMap[Number(key)] || "info",
    }));
};
